// Fetch config.json at runtime
let configCache: {
    readonly APP_MODE: 'local' | 'test' | 'production';
    readonly API_URL: string;
    readonly CLAIM_FORM_URL: string;
    readonly CLAIM_RESUME_BASE_URL: string;
    readonly PORT: number;
    readonly API_KEY: string;
} | null = null;

export const loadConfig = async (): Promise<void> => {
    if (configCache) {
        return;
    }

    try {
        const response = await fetch('/config.json');
        if (!response.ok) {
            throw new Error(`Failed to load config: ${response.statusText}`);
        }
        const siteConfig = await response.json() as {
            APP_MODE?: string;
            API_URL?: string;
            CLAIM_FORM_URL?: string;
            CLAIM_RESUME_BASE_URL?: string;
            PORT?: number;
            API_KEY?: string;
        };
        
        const appMode = (siteConfig.APP_MODE || 'local') as 'local' | 'test' | 'production';
        const claimFormUrl = siteConfig.CLAIM_FORM_URL || 'https://claims.storespace.com';
        
        configCache = {
            APP_MODE: appMode,
            API_URL: siteConfig.API_URL || 'https://claims-api.storespace.com/api',
            CLAIM_FORM_URL: claimFormUrl,
            CLAIM_RESUME_BASE_URL: siteConfig.CLAIM_RESUME_BASE_URL || claimFormUrl,
            PORT: siteConfig.PORT || 3002,
            API_KEY: siteConfig.API_KEY || '',
        } as const;
    } catch (error) {
        console.error('Error loading config.json, using defaults:', error);
        // Fallback to defaults if config.json can't be loaded
        configCache = {
            APP_MODE: 'local' as const,
            API_URL: 'http://localhost/api',
            CLAIM_FORM_URL: 'http://localhost:3001',
            CLAIM_RESUME_BASE_URL: 'http://localhost:3001',
            PORT: 3002,
            API_KEY: '',
        } as const;
    }
};

// Export config object (will be populated after loadConfig() is called)
export const config: {
    readonly APP_MODE: 'local' | 'test' | 'production';
    readonly API_URL: string;
    readonly CLAIM_FORM_URL: string;
    readonly CLAIM_RESUME_BASE_URL: string;
    readonly PORT: number;
    readonly API_KEY: string;
} = {
    get APP_MODE() {
        if (!configCache) {
            throw new Error('Config not loaded yet. Call loadConfig() first.');
        }
        return configCache.APP_MODE;
    },
    get API_URL() {
        if (!configCache) {
            throw new Error('Config not loaded yet. Call loadConfig() first.');
        }
        return configCache.API_URL;
    },
    get CLAIM_FORM_URL() {
        if (!configCache) {
            throw new Error('Config not loaded yet. Call loadConfig() first.');
        }
        return configCache.CLAIM_FORM_URL;
    },
    get CLAIM_RESUME_BASE_URL() {
        if (!configCache) {
            throw new Error('Config not loaded yet. Call loadConfig() first.');
        }
        return configCache.CLAIM_RESUME_BASE_URL;
    },
    get PORT() {
        if (!configCache) {
            throw new Error('Config not loaded yet. Call loadConfig() first.');
        }
        return configCache.PORT;
    },
    get API_KEY() {
        if (!configCache) {
            throw new Error('Config not loaded yet. Call loadConfig() first.');
        }
        return configCache.API_KEY;
    },
} as any;
